import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  InfoWindow,
  OverlayView,
  DirectionsRenderer,
} from "@react-google-maps/api";
import {
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Box
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { loadOptions } from "./mapConfig";
import DashboardHeader from "../../components/header/DashboardHeader";
import config from "../../config.json";
import NavMenu from "../../components/navbar/NavMenu";
import TitleBar from "../../components/navbar/TitleBar";
import { useTranslation } from "react-i18next";
import SubTitle from "../../components/common/SubTitle";


const colors = [
  "#20A8E0", // Light Blue
  "#8FBC8F", // Dark Sea Green
  "#FFD700", // Gold
  "#6495ED", // Cornflower Blue
  "#FF69B4", // Hot Pink
  "#FFA07A", // Light Salmon
  "#ADFF2F", // Green Yellow
  "#FF4500", // Orange Red
  "#9370DB", // Medium Purple
  "#FFFFFF", // White (Test Color)
];
const AllStopsMapPage = () => {
  const { t } = useTranslation()
  const location = useLocation();
  const allStopsData = location.state?.allStopsData || [];
  const [isLoading, setIsLoading] = useState(true);
  console.log(allStopsData);
  const [map, setMap] = useState(null);
  const [directions, setDirections] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [stops, setStops] = useState([]);
  const [selectedStop, setSelectedStop] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 0, lng: 0 });
  const [routesByOrganization, setRoutesByOrganization] = useState([]);
  const navigate = useNavigate();
  const [selectedStops, setSelectedStops] = useState([]);
  const [filteredStops, setFilteredStops] = useState([]);

  const parsedData = JSON.parse(localStorage.getItem("userState"));
  const orgId = parsedData?.user?.organizationId

  const libraries = ["places", "directions"];
  // const { isLoaded, loadError } = useJsApiLoader(loadOptions);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey,
    libraries,
  });
  const onLoad = useCallback((map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  useEffect(() => {
    if (allStopsData.length > 0) {
      // Set the map center to the first stop's location
      setMapCenter({
        lat: 13.1986,
        lng: 77.7066,
      });

    }
  }, [allStopsData]);

  const handleStopClick = (stop) => {
    setSelectedStop(stop);
  };

  const handleInfoWindowClose = () => {
    setSelectedStop(null);
  };

  const handleViewDetails = () => {
    if (selectedStop) {
      navigate(`/Stop/track`, { state: { selectedStopData: selectedStop } });
    }
  };

  // Fetch routes and their associated stops
  useEffect(() => {
    async function fetchRoutes() {
      setIsLoading(true);
      try {
        const routesResponse = await axios.get(
          `${config.baseURL}/route/organization`,
          {
            params: {
              organizationId: 1,
            },
            withCredentials: true,
          }
        );

        if (routesResponse.status === 401) {
          navigate("/");
          return;
        }

        const routesWithStops = await Promise.all(
          routesResponse.data.map(async (route) => {
            const stops = await fetchStopsForRoute(route);
            return { ...route, stops };
          })
        );

        setRoutesByOrganization(routesWithStops);
        setRoutes(routesWithStops);

        // Extract all unique stops from routes
        const allStops = routesWithStops.flatMap((route) =>
          route.stops
            .flat()
            .filter(
              (stop, index, self) =>
                index === self.findIndex((s) => s.id === stop.id)
            )
        );
        setStops(allStops);
      } catch (error) {
        if (error.response?.status === 401) {
          navigate("/");
          return;
        }
        console.error("Error fetching routes:", error);
      } finally {
        setIsLoading(false);
      }
    }
    fetchRoutes();
  }, [navigate]);

  // Function to fetch stops for a specific route coordinate
  const fetchStopsForRoute = async (route) => {
    const stops = [];
    for (const coordinate of route.routeCoordinates) {
      try {
        const stopsResponse = await axios.get(
          `${config.baseURL}/stop/organization/location`,
          {
            params: {
              // organizationId: 17,
              organizationId: orgId,
              longitude: coordinate.longitude,
              latitude: coordinate.latitude,
            },
            withCredentials: true,
          }
        );
        stops.push(stopsResponse.data);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          console.log("Unauthorized request");
          navigate("/");
          return;
        }
        console.log("Error in fetching stops by coordinates", error);
      }
    }
    return stops;
  };

  // Update directions calculation to use routesByOrganization
  useEffect(() => {
    if (isLoaded && routesByOrganization.length > 0 && map) {
      const directionsService = new window.google.maps.DirectionsService();

      const fetchDirections = async () => {
        const directionsPromises = routesByOrganization.map((route) => {
          return new Promise((resolve, reject) => {
            const waypoints = route.routeCoordinates
              .slice(1, -1)
              .map((coord) => ({
                location: new window.google.maps.LatLng(
                  parseFloat(coord.latitude),
                  parseFloat(coord.longitude)
                ),
                stopover: true,
              }));

            const origin = new window.google.maps.LatLng(
              parseFloat(route.routeCoordinates[0].latitude),
              parseFloat(route.routeCoordinates[0].longitude)
            );
            const destination = new window.google.maps.LatLng(
              parseFloat(
                route.routeCoordinates[route.routeCoordinates.length - 1]
                  .latitude
              ),
              parseFloat(
                route.routeCoordinates[route.routeCoordinates.length - 1]
                  .longitude
              )
            );

            directionsService.route(
              {
                origin: origin,
                destination: destination,
                waypoints: waypoints,
                travelMode: window.google.maps.TravelMode.DRIVING,
              },
              (result, status) => {
                if (status === window.google.maps.DirectionsStatus.OK) {
                  resolve({ ...result, routeColor: route.routeColor });
                } else {
                  reject(`error fetching directions ${result}`);
                }
              }
            );
          });
        });

        try {
          const results = await Promise.all(directionsPromises);
          setDirections(results);
        } catch (error) {
          console.error(error);
        }
      };

      fetchDirections();
    }
  }, [isLoaded, routesByOrganization, map]);

  useEffect(() => {
    if (selectedStops.length === 0) {
      setFilteredStops(allStopsData); // Show all stops if nothing is selected
    } else {
      const filtered = allStopsData.filter((stop) =>
        selectedStops.includes(stop.id)
      );
      setFilteredStops(filtered);
    }
  }, [allStopsData, selectedStops]);

  const handleStopFilter = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedStops(typeof value === "string" ? value.split(",") : value);
  };

  const mapContainerStyle = {
    width: "100%",
    height: "80vh",
  };
  const mapStyles = [
    {
      featureType: "all",
      elementType: "all",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "road",
      elementType: "geometry",
      stylers: [{ visibility: "on" }, { color: "black" }],
    },
  ];

  const renderMap = () => (
    <>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={mapCenter}
        zoom={14.5}
        options={{
          styles: mapStyles,
        }}
        onLoad={onLoad}
        onUnmount={onUnmount}
      >
        <FormControl
          size="small"
          sx={{
            position: "absolute",
            top: "20px",
            right: "20px",
            width: 200,
            // zIndex: 1000,
            backgroundColor: "white",
            borderRadius: "8px",
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#000",
                borderRadius: "8px",
              },
              "&:hover fieldset": {
                borderColor: "#000",
              },
            },
            scrollbarWidth: "thin",
            scrollbarColor: "#888 transparent",
            "& .MuiSelect-select": {
              "&::-webkit-scrollbar": {
                width: "4px",
                height: "4px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#666",
              },
            },
          }}
        >
          <Select
            multiple
            displayEmpty
            value={selectedStops}
            onChange={handleStopFilter}
            input={<OutlinedInput />}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return t("Select Stops");
              }
              return selected.length === 1
                ? allStopsData.find((stop) => stop.id === selected[0])?.name
                : `${selected.length} ${t("stops selected")}`;
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 224,
                  width: 200,
                },
                sx: {
                  borderRadius: "4px",
                  "&::-webkit-scrollbar": {
                    width: "3px",
                    height: "4px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "transparent",
                    borderRadius: "8px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#888",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: "#666",
                  },
                  scrollbarWidth: "thin",
                  scrollbarColor: "#888 transparent",
                },
              },
            }}
          >
            {allStopsData.map((stop) => (
              <MenuItem
                key={stop.id}
                value={stop.id}
                sx={{
                  borderRadius: "4px",
                  margin: "2px 4px",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                <Checkbox
                  checked={selectedStops.includes(stop.id)}
                  sx={{ padding: "0", marginBottom: "5px" }}
                />
                <ListItemText
                  primary={stop.name}
                  sx={{
                    "& .MuiTypography-root": {
                      fontSize: "14px",
                      color: "#000000",
                    },
                  }}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {filteredStops.map((stop) => (
          <React.Fragment key={stop.id}>
            <OverlayView
              position={{
                lat: parseFloat(stop.latitude),
                lng: parseFloat(stop.longitude),
              }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div
                style={{
                  position: "relative",
                  top: "-62px",
                  left: "0px",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: "white",
                    color: "black",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    fontWeight: "500",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    whiteSpace: "nowrap",
                    zIndex: 1000,
                  }}
                >
                  {stop.name}
                </div>
              </div>
            </OverlayView>
            <Marker
              position={{
                lat: parseFloat(stop.latitude),
                lng: parseFloat(stop.longitude),
              }}
              title={stop.name}
            />
          </React.Fragment>
        ))}

        {/* Render Routes */}
        {directions.map((direction, index) => (
          <DirectionsRenderer
            key={`direction-${index}`}
            directions={direction}
            options={{
              suppressMarkers: true,
              preserveViewport: true,
              polylineOptions: {
                strokeColor: direction.routeColor || "#000000",
                strokeOpacity: 0.8,
                strokeWeight: 4,
              },
            }}
          />
        ))}
      </GoogleMap>
    </>
  );

  if (loadError) return (
    <>
      {/* <DashboardHeader title="Stops > View All" /> */}
      <TitleBar />
      <NavMenu />
      <div style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        textAlign: "center"
      }}>Error loading maps.</div>
    </>
  );
  if (!isLoaded) return (<>
    {/* <DashboardHeader title="Stops > View All" /> */}
    <TitleBar />
    <NavMenu />
    <div style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      textAlign: "center"
    }}>oading maps</div>
  </>
  );
  if (allStopsData.length === 0) return (<>
    {/* <DashboardHeader title="Stops > View All" /> */}
    <TitleBar />
    <NavMenu />
    <div style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      textAlign: "center"
    }}>No stops data available.....</div>
  </>);

  return (
    <>
      {/* <DashboardHeader title="Stops > View All" /> */}
      <TitleBar />
      <NavMenu />
      <SubTitle
        title={"Monitor Stop"}
        path={"Stops > Monitor Stops"} />
      {/* <div className="page_container" style={{ border: "none" }}> */}
      <Box
        sx={{
          border: "none", position: "relative"
          ,
          margin: { xs: "0px 20px", md: "0px 40px" },
          boxShadow: "-2px 1px 5px 1px #00000040",
          borderRadius: "10px",
          padding: "20px",
          border: "1px solid #A5DCF3"
        }}
      >
        {renderMap()}
      </Box>


      {/* </div> */}
    </>
  );
};

// helper functions
const getBackgroundColor = (deviceId) => {
  const index = Math.abs(hashString(deviceId)) % colors.length;
  return colors[index];
};

const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash;
  }
  return hash;
};

const getRandomColor = () => {
  const letters = "0123456789ABCDEF";
  let color = "#";
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};
export default AllStopsMapPage;
